import React, { useState, useEffect, useRef } from 'react';
import { Video, Image, FileText, Calendar, X, Play, Wrench } from 'lucide-react';
import rrwebPlayer from 'rrweb-player';
import type { eventWithTime } from 'rrweb';
import type { SessionToolCall } from '@/lib/api/sessions';
import DomRecordingPlayer from '@/components/content-library/DomRecordingPlayer';

interface ToolCallBubbleProps {
  toolCall: SessionToolCall;
  isActive?: boolean;
  hideMedia?: boolean;
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  show_presentation_slide: 'Showed slide',
  fetch_video_segment: 'Showed video',
  replay_video: 'Replayed video',
  show_documentation: 'Showed documentation',
  schedule_meeting: 'Opened calendar',
};

function toolIcon(toolName: string) {
  switch (toolName) {
    case 'fetch_video_segment':
    case 'replay_video':
      return <Video className="h-3.5 w-3.5" />;
    case 'show_presentation_slide':
      return <Image className="h-3.5 w-3.5" />;
    case 'show_documentation':
      return <FileText className="h-3.5 w-3.5" />;
    case 'schedule_meeting':
      return <Calendar className="h-3.5 w-3.5" />;
    default:
      return <Wrench className="h-3.5 w-3.5" />;
  }
}

type LightboxState =
  | { type: 'image'; src: string }
  | { type: 'video'; src: string }
  | { type: 'dom'; sourceUrl: string; audioUrl?: string | null };

function MediaLightbox({ state, onClose }: { state: LightboxState; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 animate-in fade-in duration-150"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </button>
      <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {state.type === 'image' ? (
          <img src={state.src} alt="" className="max-w-full max-h-[90vh] object-contain rounded" />
        ) : state.type === 'video' ? (
          <video src={state.src} controls autoPlay className="max-w-full max-h-[90vh] rounded" />
        ) : (
          <div data-rrweb-container className="rounded overflow-hidden text-black">
            <DomRecordingPlayer
              sourceUrl={state.sourceUrl}
              audioUrl={state.audioUrl}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  MiniDomPlayer — lightweight inline rrweb replay for active items  */
/* ------------------------------------------------------------------ */

type DomRecordingPayload =
  | eventWithTime[]
  | { events?: eventWithTime[]; data?: eventWithTime[] };

const domEventsCache = new Map<string, eventWithTime[]>();

function parseDomPayload(payload: DomRecordingPayload): eventWithTime[] {
  if (Array.isArray(payload)) return payload;
  if (payload?.events && Array.isArray(payload.events)) return payload.events;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
}

function MiniDomPlayer({ sourceUrl }: { sourceUrl: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<rrwebPlayer | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let mounted = true;
    const container = containerRef.current;
    if (!container) return;

    function initPlayer(el: HTMLDivElement, events: eventWithTime[]) {
      if (!mounted) return;
      el.innerHTML = '';

      const meta = events.find((e) => e.type === 4);
      const recW = (meta?.data as Record<string, number>)?.width || 1280;
      const recH = (meta?.data as Record<string, number>)?.height || 720;
      const width = 280;
      const height = Math.round(width / (recW / recH));

      const player = new rrwebPlayer({
        target: el,
        props: {
          events,
          autoPlay: true,
          showController: false,
          width,
          height,
        },
      });
      playerRef.current = player;
      setStatus('ready');
    }

    const cached = domEventsCache.get(sourceUrl);
    if (cached) {
      initPlayer(container, cached);
    } else {
      fetch(sourceUrl)
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch');
          return r.json() as Promise<DomRecordingPayload>;
        })
        .then((payload) => {
          if (!mounted) return;
          const events = parseDomPayload(payload);
          if (!events.length) {
            setStatus('error');
            return;
          }
          domEventsCache.set(sourceUrl, events);
          initPlayer(container, events);
        })
        .catch(() => {
          if (mounted) setStatus('error');
        });
    }

    return () => {
      mounted = false;
      if (playerRef.current) {
        try {
          (playerRef.current as unknown as { destroy?: () => void }).destroy?.();
        } catch { /* noop */ }
        playerRef.current = null;
      }
    };
  }, [sourceUrl]);

  if (status === 'error') {
    return (
      <div className="flex items-center justify-center py-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-900">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60">
          <Play className="h-4 w-4 text-white ml-0.5" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {status === 'loading' && (
        <div className="flex items-center justify-center h-[158px] text-xs text-muted-foreground">
          Loading…
        </div>
      )}
      <div ref={containerRef} className="pointer-events-none" />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function SlideMedia({ outputs, onExpand }: { outputs: Record<string, any>; onExpand: (s: LightboxState) => void }) {
  const imageUrl = outputs.image_url;
  if (!imageUrl) return null;

  return (
    <div
      className="group cursor-pointer mt-2 rounded border border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 overflow-hidden"
      onClick={() => onExpand({ type: 'image', src: imageUrl })}
    >
      <img
        src={imageUrl}
        alt={outputs.title || 'Slide'}
        loading="lazy"
        className="w-full max-h-40 object-contain bg-white dark:bg-zinc-900"
      />
      <div className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
        <Image className="h-3 w-3" />
        <span>View slide</span>
      </div>
    </div>
  );
}

function VideoMedia({ outputs, onExpand, isActive }: { outputs: Record<string, any>; onExpand: (s: LightboxState) => void; isActive?: boolean }) {
  const videoUrl = outputs.video_url;
  const domRecordingUrl = outputs.dom_recording_url;
  const audioUrl = outputs.audio_url;

  if (!videoUrl && !domRecordingUrl) return null;

  // DOM recordings
  if (domRecordingUrl && !videoUrl) {
    // When active during audio playback, show inline mini rrweb player
    if (isActive) {
      return (
        <div
          className="group cursor-pointer mt-2 w-fit mx-auto rounded border border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 overflow-hidden"
          onClick={() => onExpand({ type: 'dom', sourceUrl: domRecordingUrl, audioUrl })}
        >
          <MiniDomPlayer sourceUrl={domRecordingUrl} />
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
            <Video className="h-3 w-3" />
            <span>Click to expand</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className="relative group cursor-pointer mt-2 rounded border border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 overflow-hidden"
        onClick={() => onExpand({ type: 'dom', sourceUrl: domRecordingUrl, audioUrl })}
      >
        <div className="flex items-center justify-center py-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-900">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 group-hover:bg-black/80 transition-colors">
            <Play className="h-4 w-4 text-white ml-0.5" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
          <Video className="h-3 w-3" />
          <span>Play recording</span>
        </div>
      </div>
    );
  }

  const startTime = outputs.start_time;
  const endTime = outputs.end_time;
  const src = startTime != null && endTime != null
    ? `${videoUrl}#t=${startTime},${endTime}`
    : videoUrl;

  return (
    <div
      className="relative group cursor-pointer mt-2 rounded border border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 overflow-hidden"
      onClick={() => onExpand({ type: 'video', src })}
    >
      <div className="flex items-center justify-center py-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-900">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 group-hover:bg-black/80 transition-colors">
          <Play className="h-4 w-4 text-white ml-0.5" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-1.5 py-1.5 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
        <Video className="h-3 w-3" />
        <span>Play recording</span>
      </div>
    </div>
  );
}

function PlaylistMedia({ outputs, onExpand, isActive }: { outputs: Record<string, any>; onExpand: (s: LightboxState) => void; isActive?: boolean }) {
  const segments = outputs.segments;
  if (!segments || !Array.isArray(segments) || segments.length === 0) return null;

  // Show first segment's video if available
  const first = segments[0];
  return <VideoMedia outputs={first} onExpand={onExpand} isActive={isActive} />;
}

const ToolCallBubble: React.FC<ToolCallBubbleProps> = ({ toolCall, isActive, hideMedia }) => {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const displayName = TOOL_DISPLAY_NAMES[toolCall.tool_name] || toolCall.tool_name;
  const outputs = toolCall.outputs_json;
  const messageType = outputs?.type;

  const renderMedia = () => {
    if (!outputs) return null;

    switch (messageType) {
      case 'presentation_slide':
        return <SlideMedia outputs={outputs} onExpand={setLightbox} />;
      case 'video_segment':
      case 'dom_segment':
        return <VideoMedia outputs={outputs} onExpand={setLightbox} isActive={isActive} />;
      case 'segment_playlist':
        return <PlaylistMedia outputs={outputs} onExpand={setLightbox} isActive={isActive} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex justify-center">
        <div className="max-w-[70%] rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900/50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {toolIcon(toolCall.tool_name)}
            <span className="font-medium">{displayName}</span>
            {outputs?.title && (
              <span className="truncate">— {outputs.title}</span>
            )}
            {outputs?.custom_label && !outputs?.title && (
              <span className="truncate">— {outputs.custom_label}</span>
            )}
          </div>
          {!hideMedia && renderMedia()}
        </div>
      </div>
      {lightbox && <MediaLightbox state={lightbox} onClose={() => setLightbox(null)} />}
    </>
  );
};

export default ToolCallBubble;
